export type LevelId = "junior" | "middle" | "senior";
export type PositionId = "backend" | "frontend" | "pm" | "designer" | "data" | "devops" | "growth" | "bd";
export type WizardStep = 1 | 2 | 3;
export type StageId = "any" | "early" | "growth" | "big";

export type Level = { id: LevelId; label: string; yearsText: string };
export type Competency = { id: string; name: string; desc: string };
export type Position = {
  id: PositionId;
  label: string;
  chipLabel: string;
  roleHint: string;
  blurb: string;
  defaultLevel: LevelId;
  responsibilities: string[];
  competencies: Competency[];
};
export type Stage = { id: StageId; label: string; desc?: string; critName?: string; critDesc?: string };
export type Domain = { id: string; label: string };
export type ExcludePreset = { id: string; label: string; rule: string };
export type Selection = {
  positionId: PositionId;
  level: LevelId;
  competencyIds: string[];
  requiredIds: string[];
  stage: StageId;
  domainIds: string[];
  excludeIds: string[];
};

export const LEVELS: Level[] = [
  { id: "junior", label: "주니어", yearsText: "1~3년" },
  { id: "middle", label: "미들", yearsText: "3~7년" },
  { id: "senior", label: "시니어", yearsText: "7년 이상" },
];

export const STAGES: Stage[] = [
  { id: "any", label: "무관" },
  {
    id: "early",
    label: "초기 스타트업",
    desc: "시드~시리즈 A",
    critName: "초기 스타트업 경험",
    critDesc: "시드~시리즈 A 단계 0→1 경험",
  },
  {
    id: "growth",
    label: "성장기",
    desc: "시리즈 B~C 스케일업",
    critName: "성장기 스타트업 경험",
    critDesc: "시리즈 B~C 스케일업 단계 경험",
  },
  { id: "big", label: "대기업·중견", desc: "안정적 규모", critName: "대기업·중견 경험", critDesc: "대규모 조직 운영 경험" },
];

export const DOMAINS: Domain[] = [
  { id: "fintech", label: "핀테크" },
  { id: "commerce", label: "커머스" },
  { id: "ai", label: "AI" },
  { id: "saas", label: "B2B SaaS" },
  { id: "content", label: "콘텐츠·미디어" },
  { id: "health", label: "헬스케어" },
  { id: "game", label: "게임" },
  { id: "mobility", label: "모빌리티·물류" },
];

export const DOMAIN_MAX = 2;

export const EXCLUDE_PRESETS: ExcludePreset[] = [
  { id: "founder", label: "현직 대표·창업자 제외", rule: "현직이 CEO/대표/Founder인 경우" },
  { id: "si", label: "SI·외주 개발사만 경력 제외", rule: "SI 회사만 경력인 경우" },
  { id: "jobhop", label: "잦은 이직(평균 1년 미만) 제외", rule: "평균 재직 1년 미만 잦은 이직" },
];

export const POSITIONS: Position[] = [
  {
    id: "backend",
    label: "백엔드 엔지니어",
    chipLabel: "백엔드",
    roleHint: "백엔드 엔지니어",
    blurb: "API·분산 시스템·대용량 트래픽",
    defaultLevel: "senior",
    responsibilities: [
      "핵심 서비스 백엔드 API 설계 및 개발",
      "트래픽 증가에 대비한 시스템 확장성·안정성 강화",
      "도메인 서비스 및 데이터 파이프라인 설계",
    ],
    competencies: [
      { id: "be-lang", name: "서버 언어 역량", desc: "Java/Kotlin/Go 기반 서비스 운영" },
      { id: "be-spring", name: "Spring 프레임워크", desc: "Spring Boot 기반 API 운영 경험" },
      { id: "be-msa", name: "MSA·분산 시스템", desc: "마이크로서비스 설계·운영 경험" },
      { id: "be-payment", name: "결제·정산 도메인", desc: "결제·정산·회계 시스템 경험" },
      { id: "be-traffic", name: "대용량 트래픽 처리", desc: "고트래픽 상황 최적화 경험" },
      { id: "be-mq", name: "이벤트 기반 아키텍처", desc: "Kafka 등 메시지 큐 운영" },
      { id: "be-db", name: "DB 설계·운영", desc: "MySQL/PostgreSQL·Redis 운영" },
      { id: "be-cloud", name: "클라우드 인프라", desc: "AWS 등 클라우드 운영 경험" },
    ],
  },
  {
    id: "frontend",
    label: "프론트엔드 엔지니어",
    chipLabel: "프론트엔드",
    roleHint: "프론트엔드 엔지니어",
    blurb: "React·Next.js·디자인 시스템",
    defaultLevel: "middle",
    responsibilities: ["메인 서비스 웹 UI 개발 및 운영", "핵심 페이지 성능 최적화", "디자인 시스템 구축 및 운영"],
    competencies: [
      { id: "fe-react", name: "React·Next.js", desc: "React/Next.js 운영 경험" },
      { id: "fe-ts", name: "TypeScript", desc: "TypeScript 기반 대규모 코드베이스" },
      { id: "fe-state", name: "상태 관리", desc: "Redux/Zustand/Recoil 등 운영" },
      { id: "fe-ds", name: "디자인 시스템", desc: "컴포넌트·디자인 시스템 구축" },
      { id: "fe-perf", name: "성능 최적화", desc: "Web Vitals·렌더링 최적화" },
      { id: "fe-ssr", name: "렌더링 전략", desc: "SSR/SSG 등 렌더링 운영" },
      { id: "fe-test", name: "테스트·품질", desc: "프론트엔드 테스트 자동화" },
      { id: "fe-commerce", name: "커머스·결제 UI", desc: "주문·결제 플로우 개발" },
    ],
  },
  {
    id: "pm",
    label: "프로덕트 매니저",
    chipLabel: "PM",
    roleHint: "프로덕트 매니저",
    blurb: "고객 인터뷰·로드맵·데이터",
    defaultLevel: "senior",
    responsibilities: ["제품 로드맵 수립 및 우선순위 결정", "고객 인터뷰 및 요구사항 정리", "엔지니어링·디자인 팀과 협업 리드"],
    competencies: [
      { id: "pm-discovery", name: "고객 발굴·인터뷰", desc: "고객 인터뷰·문제 정의 경험" },
      { id: "pm-roadmap", name: "로드맵·우선순위", desc: "로드맵 수립·우선순위 결정" },
      { id: "pm-data", name: "데이터 기반 의사결정", desc: "지표 설계·데이터 분석" },
      { id: "pm-b2b", name: "B2B SaaS 경험", desc: "기업용 제품 PM 경험" },
      { id: "pm-growth", name: "그로스·실험", desc: "A/B 테스트·퍼널 개선" },
      { id: "pm-collab", name: "협업 리드", desc: "엔지니어·디자인 협업 리드" },
      { id: "pm-global", name: "글로벌 제품", desc: "글로벌 시장 제품 경험" },
      { id: "pm-tech", name: "기술 이해도", desc: "API·기술 구조 이해" },
    ],
  },
  {
    id: "designer",
    label: "프로덕트 디자이너",
    chipLabel: "디자이너",
    roleHint: "프로덕트 디자이너",
    blurb: "Figma·디자인 시스템·UX",
    defaultLevel: "middle",
    responsibilities: ["제품 UI/UX 설계", "디자인 시스템 구축 및 컴포넌트 디자인", "사용자 리서치 결과를 디자인에 반영"],
    competencies: [
      { id: "ds-figma", name: "Figma 숙련도", desc: "Figma 기반 디자인 운영" },
      { id: "ds-system", name: "디자인 시스템", desc: "디자인 시스템 0→1 구축" },
      { id: "ds-ux", name: "UX 설계", desc: "사용자 플로우·정보구조 설계" },
      { id: "ds-research", name: "사용자 리서치", desc: "사용자 인터뷰·리서치 수행" },
      { id: "ds-interaction", name: "인터랙션·모션", desc: "마이크로 인터랙션 디자인" },
      { id: "ds-mobile", name: "모바일·웹", desc: "iOS/Android·웹 디자인 경험" },
      { id: "ds-ai", name: "AI 제품 디자인", desc: "AI/LLM 제품 UX 경험" },
      { id: "ds-brand", name: "브랜드·비주얼", desc: "브랜드·비주얼 디자인" },
    ],
  },
  {
    id: "data",
    label: "데이터 엔지니어",
    chipLabel: "데이터",
    roleHint: "데이터 엔지니어",
    blurb: "파이프라인·웨어하우스·분석",
    defaultLevel: "middle",
    responsibilities: ["데이터 파이프라인 설계 및 운영", "데이터 웨어하우스·모델링 구축", "분석 환경 및 지표 체계 구축"],
    competencies: [
      { id: "da-pipeline", name: "데이터 파이프라인", desc: "ETL/ELT 파이프라인 운영" },
      { id: "da-warehouse", name: "데이터 웨어하우스", desc: "BigQuery/Snowflake 등 운영" },
      { id: "da-sql", name: "SQL·모델링", desc: "SQL·데이터 모델링 역량" },
      { id: "da-spark", name: "대용량 처리", desc: "Spark/Airflow 등 운영" },
      { id: "da-ml", name: "머신러닝", desc: "ML 모델 개발·운영 경험" },
      { id: "da-analysis", name: "데이터 분석", desc: "지표 분석·대시보드 구축" },
      { id: "da-stream", name: "실시간 처리", desc: "스트리밍 데이터 처리" },
      { id: "da-infra", name: "데이터 인프라", desc: "데이터 인프라 운영" },
    ],
  },
  {
    id: "devops",
    label: "DevOps·인프라 엔지니어",
    chipLabel: "DevOps",
    roleHint: "DevOps 엔지니어",
    blurb: "쿠버네티스·CI/CD·클라우드",
    defaultLevel: "senior",
    responsibilities: [
      "인프라 자동화 및 CI/CD 파이프라인 운영",
      "컨테이너·오케스트레이션 환경 운영",
      "시스템 모니터링 및 안정성 관리",
    ],
    competencies: [
      { id: "do-k8s", name: "쿠버네티스", desc: "Kubernetes 운영 경험" },
      { id: "do-cicd", name: "CI/CD", desc: "CI/CD 파이프라인 구축" },
      { id: "do-iac", name: "IaC", desc: "Terraform 등 IaC 운영" },
      { id: "do-cloud", name: "클라우드 운영", desc: "AWS/GCP 인프라 운영" },
      { id: "do-observability", name: "관측성·모니터링", desc: "로깅·알림·관측성 체계" },
      { id: "do-sre", name: "SRE·안정성", desc: "SRE·장애 대응 경험" },
      { id: "do-security", name: "인프라 보안", desc: "인프라 보안·접근 제어" },
      { id: "do-cost", name: "비용 최적화", desc: "클라우드 비용 최적화" },
    ],
  },
  {
    id: "growth",
    label: "그로스 마케터",
    chipLabel: "그로스",
    roleHint: "그로스 마케터",
    blurb: "퍼포먼스·퍼널·실험",
    defaultLevel: "middle",
    responsibilities: ["유저 획득·리텐션 전략 수립 및 실행", "퍼포먼스 마케팅 캠페인 운영", "데이터 기반 그로스 실험"],
    competencies: [
      { id: "gr-performance", name: "퍼포먼스 마케팅", desc: "광고 채널 운영·최적화" },
      { id: "gr-funnel", name: "퍼널 분석", desc: "AARRR 퍼널 분석·개선" },
      { id: "gr-experiment", name: "그로스 실험", desc: "A/B 테스트 설계·실행" },
      { id: "gr-crm", name: "CRM·리텐션", desc: "CRM·리텐션 캠페인 운영" },
      { id: "gr-data", name: "데이터 분석", desc: "GA/Amplitude 등 분석" },
      { id: "gr-content", name: "콘텐츠·SEO", desc: "콘텐츠·SEO 그로스" },
      { id: "gr-brand", name: "브랜드 마케팅", desc: "브랜드 캠페인 기획" },
      { id: "gr-b2b", name: "B2B 마케팅", desc: "B2B 리드 제너레이션" },
    ],
  },
  {
    id: "bd",
    label: "사업개발 담당자",
    chipLabel: "사업개발",
    roleHint: "사업개발 담당자",
    blurb: "제휴·파트너십·세일즈",
    defaultLevel: "senior",
    responsibilities: ["신규 사업·파트너십 발굴 및 협상", "핵심 고객사 관계 관리", "사업 전략 수립 및 실행"],
    competencies: [
      { id: "bd-partnership", name: "제휴·파트너십", desc: "전략적 제휴·협상 경험" },
      { id: "bd-sales", name: "B2B 세일즈", desc: "엔터프라이즈 세일즈 경험" },
      { id: "bd-strategy", name: "사업 전략", desc: "사업 기획·전략 수립" },
      { id: "bd-account", name: "고객 관리", desc: "핵심 고객사 관계 관리" },
      { id: "bd-global", name: "글로벌 사업", desc: "해외 사업개발 경험" },
      { id: "bd-finance", name: "사업 모델링", desc: "수익 모델·재무 이해" },
      { id: "bd-market", name: "시장 분석", desc: "시장·경쟁 분석 역량" },
      { id: "bd-newbiz", name: "신사업 0→1", desc: "신규 사업 0→1 경험" },
    ],
  },
];

export function getPosition(id: PositionId): Position {
  return POSITIONS.find((position) => position.id === id) ?? POSITIONS[0];
}

export function freshSelection(positionId: PositionId): Selection {
  return {
    positionId,
    level: getPosition(positionId).defaultLevel,
    competencyIds: [],
    requiredIds: [],
    stage: "any",
    domainIds: [],
    excludeIds: [],
  };
}

function resolveSelection(selection: Selection) {
  const position = getPosition(selection.positionId);
  const level = LEVELS.find((item) => item.id === selection.level) ?? LEVELS[1];
  const stage = STAGES.find((item) => item.id === selection.stage);
  const domains = selection.domainIds
    .map((id) => DOMAINS.find((item) => item.id === id))
    .filter((item): item is Domain => Boolean(item));
  const selected = position.competencies.filter((item) => selection.competencyIds.includes(item.id));
  const requiredIds = new Set(selection.requiredIds);
  const required = selected.filter((item) => requiredIds.has(item.id));
  const preferred = selected.filter((item) => !requiredIds.has(item.id));
  return { position, level, stage, domains, required, preferred };
}

export function synthesizeJd(selection: Selection) {
  const { position, level, stage, domains, required, preferred } = resolveSelection(selection);
  const stagePrefix = stage && stage.id !== "any" ? `${stage.label}(${stage.desc}) 단계의 ` : "";
  const domainPrefix = domains.length ? `${domains.map((item) => item.label).join("·")} ` : "";
  const intro = `${stagePrefix}${domainPrefix}${position.label}를 찾고 있습니다. (${level.label} · ${level.yearsText} 경력)`;
  const qualLines = [
    `- ${level.yearsText} 이상의 ${position.roleHint} 경력`,
    ...required.map((item) => `- ${item.name} — ${item.desc}`),
  ];
  const prefLines = [
    ...preferred.map((item) => `- ${item.name} — ${item.desc}`),
    ...domains.map((item) => `- ${item.label} 도메인 경험`),
    ...(stage && stage.id !== "any" && stage.critName ? [`- ${stage.critName}`] : []),
  ];

  return [
    intro,
    "",
    "담당 업무",
    position.responsibilities.map((item) => `- ${item}`).join("\n"),
    "",
    "자격 요건",
    qualLines.join("\n"),
    "",
    "우대 사항",
    (prefLines.length ? prefLines : ["- 관련 도메인 및 유관 경험"]).join("\n"),
  ].join("\n");
}

export function summarizeSelection(selection: Selection) {
  return {
    position: selection.positionId,
    level: selection.level,
    competency_count: selection.competencyIds.length,
    required_count: selection.requiredIds.length,
    domain_count: selection.domainIds.length,
    stage: selection.stage,
    exclude_count: selection.excludeIds.length,
    method: "quickstart",
  };
}
